import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  Users, BookOpen, Award, TrendingUp, Download, RefreshCw,
  Calendar, Activity, Eye, FileText, Target, Clock, AlertCircle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

const StatCard = ({ title, value, icon: Icon, change, color = "blue" }) => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 backdrop-blur-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-slate-400 text-sm font-medium">{title}</p>
        <p className={`text-2xl font-bold text-${color}-400 mt-1`}>{value}</p>
        {change && (
          <p className={`text-sm mt-1 ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change > 0 ? '+' : ''}{change} hoje
          </p>
        )}
      </div>
      <div className={`p-3 bg-${color}-500/20 rounded-lg`}>
        <Icon className={`w-6 h-6 text-${color}-400`} />
      </div>
    </div>
  </div>
);

const ExportButton = ({ data, filename, title }) => {
  const exportToExcel = () => {
    try {
      if (!Array.isArray(data) || data.length === 0) {
        alert('Nenhum dado disponível para exportação.');
        return;
      }
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title);
      
      // Gerar buffer
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(dataBlob, `${filename}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    }
  };

  return (
    <button
      onClick={exportToExcel}
      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
    >
      <Download className="w-4 h-4" />
      Exportar Excel
    </button>
  );
};

const DataTable = ({ data, title, filename }) => {
  // Verificação mais robusta
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-slate-400">Nenhum dado disponível</p>
      </div>
    );
  }

  // Verificar se o primeiro item tem dados válidos
  const firstItem = data[0];
  if (!firstItem || typeof firstItem !== 'object' || firstItem === null) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-slate-400">Dados inválidos</p>
      </div>
    );
  }

  let columns;
  try {
    columns = Object.keys(firstItem);
  } catch (error) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-slate-400">Erro ao processar dados</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <ExportButton data={data} filename={filename} title={title} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-600">
              {columns.map((column) => (
                <th key={column} className="text-left py-3 px-4 text-slate-300 font-medium">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 10).map((row, index) => (
              <tr key={index} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                {columns.map((column) => (
                  <td key={column} className="py-3 px-4 text-slate-200">
                    {row[column]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length > 10 && (
          <p className="text-slate-400 text-sm mt-4 text-center">
            Mostrando 10 de {data.length} registros. Exporte para ver todos.
          </p>
        )}
      </div>
    </div>
  );
};

const Estatisticas = () => {
  const { isAuthenticated, isAdmin, usuario } = useAuth();
  const [stats, setStats] = useState({});
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [coursesEnrollment, setCoursesEnrollment] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [certificatesList, setCertificatesList] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setError(null);
      
      // Verificar se o usuário está autenticado e é admin
      if (!isAuthenticated() || !isAdmin()) {
        setError('Acesso negado. Você precisa estar logado como administrador.');
        setLoading(false);
        return;
      }

      console.log('Iniciando busca de estatísticas...');
      console.log('Usuário:', usuario);
      console.log('Token:', localStorage.getItem('token') ? 'Existe' : 'Não existe');

      // Buscar todas as estatísticas em paralelo usando o service api
      const requests = [
        api.get('/admin/stats/dashboard').catch(e => ({ error: e })),
        api.get('/admin/stats/user-registrations').catch(e => ({ error: e })),
        api.get('/admin/stats/courses-enrollment').catch(e => ({ error: e })),
        api.get('/admin/stats/certificates').catch(e => ({ error: e })),
        api.get('/admin/stats/users-list').catch(e => ({ error: e })),
        api.get('/admin/stats/courses-list').catch(e => ({ error: e })),
        api.get('/admin/stats/certificates-list').catch(e => ({ error: e })),
        api.get('/admin/stats/online-users').catch(e => ({ error: e }))
      ];

      const responses = await Promise.all(requests);

      // Processar cada resposta de forma segura
      const processResponse = (response, defaultValue) => {
        console.log('Processando resposta:', response);
        if (response.error) {
          console.warn('Erro na resposta:', response.error);
          return defaultValue;
        }
        return response.data || defaultValue;
      };

      const [
        dashboardData,
        registrationsData,
        enrollmentData,
        certificatesData,
        usersListData,
        coursesListData,
        certificatesListData,
        onlineData
      ] = [
        processResponse(responses[0], {}),
        processResponse(responses[1], []),
        processResponse(responses[2], []),
        processResponse(responses[3], []),
        processResponse(responses[4], []),
        processResponse(responses[5], []),
        processResponse(responses[6], []),
        processResponse(responses[7], { usuarios_online: 0 })
      ];

      console.log('Dados recebidos:', {
        dashboard: dashboardData,
        registrations: registrationsData,
        enrollment: enrollmentData,
        certificates: certificatesData,
        users: usersListData,
        courses: coursesListData,
        certificatesList: certificatesListData,
        online: onlineData
      });

      // Definir dados com valores padrão seguros
      setStats({
        total_usuarios: dashboardData.total_usuarios || 0,
        total_cursos: dashboardData.total_cursos || 0,
        total_matriculas: dashboardData.total_matriculas || 0,
        total_certificados: dashboardData.total_certificados || 0,
        usuarios_hoje: dashboardData.usuarios_hoje || 0,
        certificados_hoje: dashboardData.certificados_hoje || 0,
        taxa_conclusao: dashboardData.taxa_conclusao || 0
      });
      
      setUserRegistrations(Array.isArray(registrationsData) ? registrationsData : []);
      setCoursesEnrollment(Array.isArray(enrollmentData) ? enrollmentData : []);
      setCertificates(Array.isArray(certificatesData) ? certificatesData : []);
      setUsersList(Array.isArray(usersListData) ? usersListData : []);
      setCoursesList(Array.isArray(coursesListData) ? coursesListData : []);
      setCertificatesList(Array.isArray(certificatesListData) ? certificatesListData : []);
      setOnlineUsers(onlineData?.usuarios_online || 0);

      console.log('Estados atualizados com sucesso');

    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setError('Erro ao carregar dados. Verifique sua conexão e tente novamente.');
      
      // Definir valores padrão em caso de erro total
      setStats({
        total_usuarios: 0,
        total_cursos: 0,
        total_matriculas: 0,
        total_certificados: 0,
        usuarios_hoje: 0,
        certificados_hoje: 0,
        taxa_conclusao: 0
      });
      setUserRegistrations([]);
      setCoursesEnrollment([]);
      setCertificates([]);
      setUsersList([]);
      setCoursesList([]);
      setCertificatesList([]);
      setOnlineUsers(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  useEffect(() => {
    fetchData();
    
    // Atualizar usuários online a cada 30 segundos
    const interval = setInterval(async () => {
      if (isAuthenticated() && isAdmin()) {
        try {
          const response = await api.get('/admin/stats/online-users');
          setOnlineUsers(response.data.usuarios_online);
        } catch (error) {
          console.error('Erro ao atualizar usuários online:', error);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  // Mostrar erro se não for admin
  if (!isAuthenticated() || !isAdmin()) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Acesso Negado</h2>
            <p className="text-slate-400">Você precisa estar logado como administrador para acessar esta página.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Mostrar loading
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-16 h-16 text-blue-400 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold text-white mb-2">Carregando Estatísticas</h2>
            <p className="text-slate-400">Aguarde enquanto buscamos os dados...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Mostrar erro se houver
  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Erro ao Carregar Dados</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const exportAllData = () => {
    try {
      const wb = XLSX.utils.book_new();
      
      // Aba de estatísticas gerais
      const statsSheet = XLSX.utils.json_to_sheet([{
        'Total Usuários': stats.total_usuarios || 0,
        'Total Cursos': stats.total_cursos || 0,
        'Total Matrículas': stats.total_matriculas || 0,
        'Total Certificados': stats.total_certificados || 0,
        'Usuários Hoje': stats.usuarios_hoje || 0,
        'Certificados Hoje': stats.certificados_hoje || 0,
        'Taxa de Conclusão (%)': stats.taxa_conclusao || 0,
        'Usuários Online': onlineUsers || 0
      }]);
      XLSX.utils.book_append_sheet(wb, statsSheet, 'Estatísticas Gerais');
      
      // Aba de usuários
      if (Array.isArray(usersList) && usersList.length > 0) {
        const usersSheet = XLSX.utils.json_to_sheet(usersList);
        XLSX.utils.book_append_sheet(wb, usersSheet, 'Usuários');
      }
      
      // Aba de cursos
      if (Array.isArray(coursesList) && coursesList.length > 0) {
        const coursesSheet = XLSX.utils.json_to_sheet(coursesList);
        XLSX.utils.book_append_sheet(wb, coursesSheet, 'Cursos');
      }
      
      // Aba de certificados
      if (Array.isArray(certificatesList) && certificatesList.length > 0) {
        const certificatesSheet = XLSX.utils.json_to_sheet(certificatesList);
        XLSX.utils.book_append_sheet(wb, certificatesSheet, 'Certificados');
      }
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const today = new Date().toISOString().split('T')[0];
      saveAs(dataBlob, `relatorio_completo_${today}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Estatísticas da Plataforma</h1>
            <p className="text-slate-400 mt-1">Análise completa dos dados da plataforma</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              onClick={exportAllData}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar Tudo
            </button>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total de Usuários"
            value={stats.total_usuarios || 0}
            icon={Users}
            change={stats.usuarios_hoje}
            color="blue"
          />
          <StatCard
            title="Total de Cursos"
            value={stats.total_cursos || 0}
            icon={BookOpen}
            color="green"
          />
          <StatCard
            title="Total de Matrículas"
            value={stats.total_matriculas || 0}
            icon={Target}
            color="yellow"
          />
          <StatCard
            title="Certificados Emitidos"
            value={stats.total_certificados || 0}
            icon={Award}
            change={stats.certificados_hoje}
            color="purple"
          />
        </div>

        {/* Cards Secundários */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Taxa de Conclusão"
            value={`${stats.taxa_conclusao || 0}%`}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Usuários Online"
            value={onlineUsers}
            icon={Activity}
            color="red"
          />
          <StatCard
            title="Última Atualização"
            value={new Date().toLocaleTimeString('pt-BR')}
            icon={Clock}
            color="slate"
          />
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de Cadastros */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Cadastros nos Últimos 30 Dias</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userRegistrations}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="data_formatada" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                />
                <Area type="monotone" dataKey="total" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de Certificados */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Certificados Emitidos (30 Dias)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={certificates}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="data_formatada" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                />
                <Line type="monotone" dataKey="total" stroke="#8B5CF6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de Cursos Populares */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Cursos Mais Populares</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={coursesEnrollment} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9CA3AF" />
              <YAxis type="category" dataKey="titulo" stroke="#9CA3AF" width={200} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="total_matriculas" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabelas de Dados */}
        <div className="space-y-6">
          <DataTable 
            data={usersList} 
            title="Lista de Usuários" 
            filename="usuarios" 
          />
          
          <DataTable 
            data={coursesList} 
            title="Lista de Cursos" 
            filename="cursos" 
          />
          
          <DataTable 
            data={certificatesList} 
            title="Lista de Certificados" 
            filename="certificados" 
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default Estatisticas; 